/**
 * =============================================================================
 * TYPES FOR AGENTIC TRIAGE ENTITY TABLE
 * =============================================================================
 *
 * These types are used by the entity table components and are based on the
 * getTopDetectionEntitiesInsightsPaginated GraphQL query response.
 *
 * @see useGetTopDetectionEntitiesInsightsPaginated
 * =============================================================================
 */

export interface DetectionCountsByMitreTactic {
  mitre_tactic: string;
  detection_count: number;
}

export interface DetectionCountsByStatus {
  open: number | null;
  closed: number | null;
}

export interface DetectionCountsBySeverityLevel {
  low: number | null;
  medium: number | null;
  high: number | null;
}

export enum EntityLocality {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL',
  NA = 'NA',
}

export enum AIInsightVerdict {
  LIKELY_MALICIOUS = 'LIKELY_MALICIOUS',
  LIKELY_BENIGN = 'LIKELY_BENIGN',
  SUSPICIOUS = 'SUSPICIOUS',
  UNDETERMINED = 'UNDETERMINED',
}

export interface AIInsights {
  ai_insight_verdict: AIInsightVerdict | null;
  short_description: string | null;
  ai_insight_report_id: string | null;
  created_timestamp: number | null;
}

export interface DetectionSummaryInsights {
  entity_id: string | null;
  entity_name: string | null;
  entity_type: string | null;
  entity_category: string | null;
  max_severity: number | null;
  tenant: string | null;
  tenant_entity: string | null;
  total_detections: number | null;
  most_severe_alert_category: string | null;
  detection_counts_by_mitre_tactics: DetectionCountsByMitreTactic[] | null;
  detection_counts_by_status: DetectionCountsByStatus | null;
  detection_counts_by_severity_level: DetectionCountsBySeverityLevel | null;
  comment_count: number | null;
  entity_locality: EntityLocality | null;
  ai_insights: AIInsights | null;
}

export interface PaginatedDetectionSummaryInsights {
  offset: number;
  size: number;
  summary_insights: DetectionSummaryInsights[];
  total_items: number;
}

// Re-export query-specific types from the hook
export type {
  IGetTopDetectionEntitiesInsightsPaginated,
  GetTopDetectionEntitiesInsightsPaginatedVariables,
} from '../../detections/queries/useGetTopDetectionEntitiesInsightsPaginated';

/**
 * MITRE ATT&CK stages (for reference/display purposes)
 */
export enum MitreStage {
  INITIAL_ACCESS = 'initial_access',
  EXECUTION = 'execution',
  DEFENSE_EVASION = 'defense_evasion',
  CREDENTIAL_ACCESS = 'credential_access',
  DISCOVERY = 'discovery',
  LATERAL_MOVEMENT = 'lateral_movement',
  COMMAND_AND_CONTROL = 'command_and_control',
  EXFILTRATION = 'exfiltration',
}
